import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BackofficeLayout } from '../BackofficeLayout';
import {
  Button,
  Checkbox,
  Drawer,
  Icon,
  Modal,
  PageHeading,
  Pills,
  Search,
  Select,
  Table,
  Tabs,
  Text,
  useToast,
} from 'barte-design-system';
import type { ColumnDefinition } from 'barte-design-system';
import {
  ArrowRight,
  CaretDown,
  Certificate,
  ClockCounterClockwise,
  Plus,
  Storefront,
} from '@phosphor-icons/react';
import {
  INITIAL_PLANOS,
  type PlanoTaxa,
} from '../CadastroPlanoTaxas/sharedData';
import './DetalhePlanoSheetsPreview.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type TipoPagamentoTab = 'credito-online' | 'credito-pos';

interface RatesByGroup {
  groupId: string;
  groupLabel: string;
  mccDefault: string;
  mccs: string[];
  tipo: 'online' | 'pos' | 'tap';
  antecipacao: string;
  debitoRow: number[]; // 5 valores por bandeira (linha extra do grid)
  isDefault?: boolean;
  pix: { tempo: string; tipo: 'Fixo' | '%'; taxa: string; limite: string };
  boleto: { tempo: string; tipo: 'Fixo' | '%'; taxa: string; limite: string };
  debito: { tempo: string; tipo: 'Fixo' | '%'; taxa: string };
  credito: {
    online: number[][]; // 4 rows × 5 cols
    pos: number[][];
  };
}

const BANDEIRAS = ['Mastercard', 'Visa', 'Elo', 'Amex', 'Hiper'];
const PARCELAS = ['1x', '2 a 6x', '7 a 12x', '13 a 24x'];
// Versão expandida: mostra cada parcela individualmente (1x, 2x, …, 24x)
const PARCELAS_EXPANDED = Array.from({ length: 24 }, (_, i) => `${i + 1}x`);

/** Mapeia o índice da parcela individual (0..23) para o índice do range
 *  na matriz original (0=1x, 1=2-6x, 2=7-12x, 3=13-24x). */
function parcelaIdxToRangeIdx(parcelaIdx: number): number {
  if (parcelaIdx === 0) return 0;
  if (parcelaIdx <= 5) return 1;
  if (parcelaIdx <= 11) return 2;
  return 3;
}

const REPASSE_DAYS = ['D+1', 'D+2', 'D+3', 'D+10', 'D+14', 'D+30'];
// Custo de antecipação por dia de repasse (D+30 = base, sem antecipação)
const ANTECIPACAO_DELTA: Record<string, number> = {
  'D+1':  0.50,
  'D+2':  0.42,
  'D+3':  0.35,
  'D+10': 0.22,
  'D+14': 0.15,
  'D+30': 0.00,
};

// Mock de lojistas para o modal "Lojistas vinculados" — vínculo "this" = neste plano;
// vínculo "other" = em outro plano (mostra qual).
type LojistaVinculo = 'this' | 'other';
interface Lojista {
  id: string;
  nome: string;
  cnpj: string;
  vinculo: LojistaVinculo;
  planoNome?: string;
}
const LOJISTAS_MOCK: Lojista[] = [
  { id: 'SLR-00001', nome: 'Mercado Bom Preço LTDA',          cnpj: '12.345.678/0001-91', vinculo: 'other', planoNome: 'Plano inicial' },
  { id: 'SLR-00002', nome: 'Padaria Pão Quente ME',           cnpj: '23.456.789/0001-02', vinculo: 'other', planoNome: 'Plano inicial' },
  { id: 'SLR-00003', nome: 'Loja Central de Departamentos',   cnpj: '34.567.890/0001-13', vinculo: 'other', planoNome: 'Plano inicial' },
  { id: 'SLR-00004', nome: 'Tech Store Eletrônicos S.A.',     cnpj: '45.678.901/0001-24', vinculo: 'other', planoNome: 'Plano Antecipado' },
  { id: 'SLR-00005', nome: 'FitLife Academia Esportes',       cnpj: '56.789.012/0001-35', vinculo: 'other', planoNome: 'Plano Antecipado' },
  { id: 'SLR-00006', nome: 'Ótica Visão Clara LTDA',          cnpj: '67.890.123/0001-46', vinculo: 'other', planoNome: 'Ajuste de Taxas' },
  { id: 'SLR-00007', nome: 'Pet Shop Animal Love',            cnpj: '78.901.234/0001-57', vinculo: 'other', planoNome: 'Ajuste de Taxas' },
  { id: 'SLR-00008', nome: 'Barbearia Corte Certo',           cnpj: '89.012.345/0001-68', vinculo: 'other', planoNome: 'Plano Fluxo do Tempo' },
];

type LojistasTab = 'neste' | 'outro';

// Mock determinístico — diferentes valores por grupo pra UX feliz
const RATES_MOCK: RatesByGroup[] = [
  {
    groupId: 'grp-1',
    groupLabel: 'Grupo 1 — Saúde',
    mccDefault: '8999',
    mccs: ['8999'],
    tipo: 'online',
    antecipacao: '130% da CDI',
    debitoRow: [0.95, 1.05, 1.20, 0.75, 1.10],
    isDefault: true,
    pix: { tempo: 'D + 1', tipo: 'Fixo', taxa: 'R$ 0,70', limite: 'R$ 10.000,00' },
    boleto: { tempo: 'D + 2', tipo: 'Fixo', taxa: 'R$ 1,50', limite: 'R$ 20.000,00' },
    debito: { tempo: 'D + 1', tipo: '%', taxa: '1,05%' },
    credito: {
      online: [
        [1.05, 1.37, 3.04, 0.83, 2.32],
        [0.34, 2.23, 3.51, 1.11, 3.57],
        [0.46, 2.96, 3.73, 1.11, 3.97],
        [3.45, 3.45, 3.45, 3.45, 3.45],
      ],
      pos: [
        [1.20, 1.55, 3.20, 0.95, 2.50],
        [0.50, 2.40, 3.70, 1.30, 3.75],
        [0.65, 3.15, 3.95, 1.30, 4.15],
        [3.65, 3.65, 3.65, 3.65, 3.65],
      ],
    },
  },
  {
    groupId: 'grp-2',
    groupLabel: 'Grupo 2 — Tecnologia',
    mccDefault: '7372',
    mccs: ['7372', '5732'],
    tipo: 'online',
    antecipacao: '125% da CDI',
    debitoRow: [0.88, 0.95, 1.05, 0.68, 1.00],
    pix: { tempo: 'D + 1', tipo: 'Fixo', taxa: 'R$ 0,50', limite: 'R$ 25.000,00' },
    boleto: { tempo: 'D + 1', tipo: '%', taxa: '0,80%', limite: 'R$ 15.000,00' },
    debito: { tempo: 'D + 1', tipo: '%', taxa: '0,95%' },
    credito: {
      online: [
        [0.95, 1.25, 2.85, 0.75, 2.15],
        [0.28, 2.05, 3.30, 1.00, 3.40],
        [0.40, 2.75, 3.55, 1.05, 3.80],
        [3.20, 3.20, 3.20, 3.20, 3.20],
      ],
      pos: [
        [1.10, 1.40, 3.05, 0.90, 2.35],
        [0.45, 2.25, 3.55, 1.20, 3.60],
        [0.60, 2.95, 3.80, 1.25, 4.00],
        [3.45, 3.45, 3.45, 3.45, 3.45],
      ],
    },
  },
  {
    groupId: 'grp-3',
    groupLabel: 'Grupo 3 — Varejo',
    mccDefault: '5411',
    mccs: ['5411', '5912', '5651', '5944', '5999'],
    tipo: 'pos',
    antecipacao: '120% da CDI',
    debitoRow: [1.00, 1.10, 1.20, 0.80, 1.15],
    pix: { tempo: 'D + 0', tipo: 'Fixo', taxa: 'R$ 0,40', limite: 'R$ 50.000,00' },
    boleto: { tempo: 'D + 2', tipo: 'Fixo', taxa: 'R$ 1,80', limite: 'R$ 50.000,00' },
    debito: { tempo: 'D + 1', tipo: '%', taxa: '1,20%' },
    credito: {
      online: [
        [1.15, 1.45, 3.15, 0.90, 2.40],
        [0.40, 2.30, 3.60, 1.20, 3.65],
        [0.55, 3.05, 3.80, 1.20, 4.05],
        [3.55, 3.55, 3.55, 3.55, 3.55],
      ],
      pos: [
        [1.30, 1.65, 3.35, 1.05, 2.60],
        [0.55, 2.50, 3.85, 1.40, 3.85],
        [0.70, 3.25, 4.05, 1.40, 4.25],
        [3.75, 3.75, 3.75, 3.75, 3.75],
      ],
    },
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function DetalhePlanoSheetsPreview() {
  const navigate = useNavigate();
  const { planoId } = useParams<{ planoId: string }>();

  // Resolve plano da lista mock
  const plano: PlanoTaxa | undefined = useMemo(() => {
    const id = parseInt(planoId ?? '', 10);
    return INITIAL_PLANOS.find((p) => p.id === id);
  }, [planoId]);

  const planoNome = plano?.nome ?? 'Plano criado';

  // Filtros
  const [grupoId, setGrupoId] = useState<string>(RATES_MOCK[0].groupId);
  const [tipoPagamento] = useState<TipoPagamentoTab>('credito-online');
  // Dia de repasse selecionado nas tabs do topo
  const [selectedRepasseDay, setSelectedRepasseDay] = useState<string>('D+1');
  // Multiplicador de antecipação por dia (D+30 = sem antecipação)
  const DAY_MULTIPLIER: Record<string, number> = {
    'D+1':  1.00,
    'D+2':  0.85,
    'D+3':  0.75,
    'D+10': 0.50,
    'D+14': 0.40,
    'D+30': 0.00,
  };
  // Base de antecipação por grupo (delta em %, somado ao MDR pra obter CET)
  const ANTECIPACAO_BASE_BY_GROUP: Record<string, number> = {
    'grp-1': 2.00,
    'grp-2': 1.60,
    'grp-3': 1.50,
  };
  const [defaultGroupId, setDefaultGroupId] = useState<string>(
    RATES_MOCK.find((g) => g.isDefault)?.groupId ?? RATES_MOCK[0].groupId,
  );

  // Drawer de taxas detalhadas por célula clicada
  const [cellDrawerOpen, setCellDrawerOpen] = useState(false);
  const [drawerParcelaIdx, setDrawerParcelaIdx] = useState(0);
  const [drawerBandeiraIdx, setDrawerBandeiraIdx] = useState(0);

  // Modais
  const [sellersModalOpen, setSellersModalOpen] = useState(false);
  const [historicoModalOpen, setHistoricoModalOpen] = useState(false);
  const [confirmDefaultGroupId, setConfirmDefaultGroupId] = useState<string | null>(null);

  // ── Histórico de alterações ────────────────────────────────────────────────
  interface HistoricoEntry {
    id: string;
    date: string;
    author: string;
  }
  const HISTORICO_ENTRIES: HistoricoEntry[] = [
    { id: 'h1', date: '10/06/2026 14:32', author: 'Fabiano Meneghetti' },
    { id: 'h2', date: '08/06/2026 09:15', author: 'Ana Silva' },
    { id: 'h3', date: '01/06/2026 16:48', author: 'Carlos Mendes' },
    { id: 'h4', date: '25/05/2026 11:00', author: 'Fabiano Meneghetti' },
  ];
  const [historicoDropdownOpen, setHistoricoDropdownOpen] = useState(false);
  const [viewingHistoric, setViewingHistoric] = useState<HistoricoEntry | null>(null);
  const historicoPillRef = useRef<HTMLDivElement>(null);

  // Toast
  const toast = useToast();

  // Modal "Lojistas vinculados"
  const [lojistasTab, setLojistasTab] = useState<LojistasTab>('neste');
  const [lojistasState, setLojistasState] = useState<Lojista[]>(LOJISTAS_MOCK);
  const [lojistasOriginal, setLojistasOriginal] = useState<Lojista[]>(LOJISTAS_MOCK);
  const [selectedLojistaIds, setSelectedLojistaIds] = useState<Set<string>>(new Set());
  const [lojistasQuery, setLojistasQuery] = useState('');
  const [lojistasSearchExpanded, setLojistasSearchExpanded] = useState(false);

  const lojistasCountThis = useMemo(
    () => lojistasState.filter((l) => l.vinculo === 'this').length,
    [lojistasState],
  );
  const lojistasFiltered = useMemo(() => {
    const base = lojistasTab === 'neste'
      ? lojistasState.filter((l) => l.vinculo === 'this')
      : lojistasState.filter((l) => l.vinculo === 'other');
    const q = lojistasQuery.trim().toLowerCase();
    if (!q) return base;
    return base.filter((l) =>
      l.nome.toLowerCase().includes(q) ||
      l.cnpj.toLowerCase().includes(q) ||
      (l.planoNome ?? '').toLowerCase().includes(q),
    );
  }, [lojistasState, lojistasTab, lojistasQuery]);

  // Modo de ação derivado do primeiro item selecionado:
  // 'vincular' se forem itens de outro plano; 'desvincular' se forem deste plano.
  const lojistaActionMode: 'vincular' | 'desvincular' | null = useMemo(() => {
    if (selectedLojistaIds.size === 0) return null;
    const firstId = selectedLojistaIds.values().next().value;
    const l = lojistasState.find((x) => x.id === firstId);
    if (!l) return null;
    return l.vinculo === 'other' ? 'vincular' : 'desvincular';
  }, [selectedLojistaIds, lojistasState]);

  const lojistasHasChanges = useMemo(() => {
    if (lojistasState.length !== lojistasOriginal.length) return true;
    return lojistasState.some((l, i) => l.vinculo !== lojistasOriginal[i].vinculo);
  }, [lojistasState, lojistasOriginal]);

  function toggleLojistaSelection(id: string, vinculo: LojistaVinculo) {
    setSelectedLojistaIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }
      // Não permite misturar vínculos diferentes
      if (lojistaActionMode === 'vincular' && vinculo !== 'other') return prev;
      if (lojistaActionMode === 'desvincular' && vinculo !== 'this') return prev;
      next.add(id);
      return next;
    });
  }

  function applyLojistasAction() {
    if (!lojistaActionMode) return;
    const targetVinculo: LojistaVinculo = lojistaActionMode === 'vincular' ? 'this' : 'other';
    setLojistasState((prev) =>
      prev.map((l) =>
        selectedLojistaIds.has(l.id)
          ? {
              ...l,
              vinculo: targetVinculo,
              planoNome: targetVinculo === 'other' ? (l.planoNome ?? 'Outro plano') : undefined,
            }
          : l,
      ),
    );
    setSelectedLojistaIds(new Set());
  }

  function openLojistasModal() {
    setLojistasOriginal(lojistasState);
    setSellersModalOpen(true);
  }

  function cancelLojistasModal() {
    setLojistasState(lojistasOriginal);
    setSelectedLojistaIds(new Set());
    setLojistasTab('neste');
    setLojistasQuery('');
    setLojistasSearchExpanded(false);
    setSellersModalOpen(false);
  }

  function saveAndCloseLojistasModal() {
    setSelectedLojistaIds(new Set());
    setLojistasTab('neste');
    setLojistasQuery('');
    setLojistasSearchExpanded(false);
    setSellersModalOpen(false);
    toast.success({
      title: 'Lojistas atualizados com sucesso',
      description: 'A vinculação dos lojistas a este plano foi salva.',
    });
  }

  // Select-all: marca/desmarca todos os lojistas visíveis no tab atual
  // respeitando o modo de ação (só permite selecionar do mesmo vínculo).
  const selectAllEligibleIds = useMemo(() => {
    let pool = lojistasFiltered;
    // Se já há modo definido, só aceita do mesmo vínculo
    if (lojistaActionMode === 'vincular') pool = pool.filter((l) => l.vinculo === 'other');
    else if (lojistaActionMode === 'desvincular') pool = pool.filter((l) => l.vinculo === 'this');
    return pool.map((l) => l.id);
  }, [lojistasFiltered, lojistaActionMode]);

  const selectAllState: 'all' | 'some' | 'none' = useMemo(() => {
    if (selectAllEligibleIds.length === 0) return 'none';
    const allChecked = selectAllEligibleIds.every((id) => selectedLojistaIds.has(id));
    if (allChecked) return 'all';
    const anyChecked = selectAllEligibleIds.some((id) => selectedLojistaIds.has(id));
    return anyChecked ? 'some' : 'none';
  }, [selectAllEligibleIds, selectedLojistaIds]);

  function toggleSelectAllLojistas() {
    if (selectAllState === 'all') {
      // desmarcar todos os elegíveis
      setSelectedLojistaIds((prev) => {
        const next = new Set(prev);
        for (const id of selectAllEligibleIds) next.delete(id);
        return next;
      });
    } else {
      // marcar todos os elegíveis. Se ainda não há modo, define pelo 1º elegível.
      if (selectAllEligibleIds.length === 0) return;
      // Se modo ainda é null, escolhe pelo vínculo do tab atual ou do primeiro item
      const first = lojistasFiltered.find((l) => selectAllEligibleIds.includes(l.id));
      const needVinculo: LojistaVinculo | null = lojistaActionMode === 'vincular'
        ? 'other'
        : lojistaActionMode === 'desvincular'
          ? 'this'
          : first?.vinculo ?? null;
      setSelectedLojistaIds((prev) => {
        const next = new Set(prev);
        for (const id of selectAllEligibleIds) {
          const l = lojistasState.find((x) => x.id === id);
          if (!l) continue;
          if (needVinculo && l.vinculo !== needVinculo) continue;
          next.add(id);
        }
        return next;
      });
    }
  }

  // Click-outside no dropdown de histórico
  useEffect(() => {
    if (!historicoDropdownOpen) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (historicoPillRef.current && !historicoPillRef.current.contains(target)) {
        setHistoricoDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [historicoDropdownOpen]);

  // Para cada entrada de histórico, simulamos um delta nas células
  // pra evidenciar a diferença com a versão atual.
  function historicMatrix(entryId: string, current: number[][]): number[][] {
    const delta = entryId === 'h1' ? -0.10 :
                  entryId === 'h2' ? +0.15 :
                  entryId === 'h3' ? -0.25 :
                                     +0.30;
    return current.map((row) => row.map((v) => Math.max(0, v + delta)));
  }

  function exitHistoricView() {
    setViewingHistoric(null);
  }

  function labelGrupo(groupId: string): string {
    const idx = RATES_MOCK.findIndex((g) => g.groupId === groupId);
    return idx >= 0 ? `MCCs - Grupo ${idx + 1}` : groupId;
  }

  // ── Modo de edição ──────────────────────────────────────────────────────────
  type EditState = {
    miniCards: {
      pix: string;
      boleto: string;
      antifraude: string;
      gateway: string;
    };
    matrix: string[][]; // 4 × 5 — espelha o creditoMatrix em string
  };
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState<EditState | null>(null);

  function cancelEdit() {
    setEditMode(false);
    setEditValues(null);
  }
  function saveEdit() {
    setEditMode(false);
    setEditValues(null);
  }

  // ── Dropdown "Editar" (Individual / Em massa) ──────────────────────────────
  const [editDropdownOpen, setEditDropdownOpen] = useState(false);
  const headerWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editDropdownOpen) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (headerWrapRef.current && !headerWrapRef.current.contains(target)) {
        setEditDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [editDropdownOpen]);
  const grupoAtual = useMemo(
    () => RATES_MOCK.find((g) => g.groupId === grupoId) ?? RATES_MOCK[0],
    [grupoId],
  );

  const creditoMatrix =
    tipoPagamento === 'credito-online'
      ? grupoAtual.credito.online
      : grupoAtual.credito.pos;

  function handleBack() {
    navigate('/backoffice/taxas-company/copia-cola');
  }

  return (
    <>
    <BackofficeLayout activeItem="taxas-company-copia-cola">
      <main className="content">
        <div className="content-inner dpt-page">
          <div className="dpt-page-header-wrap" ref={headerWrapRef}>
            <PageHeading
              title={planoNome}
              onBack={editMode ? undefined : handleBack}
              backButtonLabel="Voltar"
              secondaryActionLabel={editMode ? 'Cancelar' : undefined}
              onSecondaryAction={editMode ? cancelEdit : undefined}
              primaryActionLabel={editMode ? 'Salvar' : undefined}
              onPrimaryAction={editMode ? saveEdit : undefined}
            />
            {!editMode && (
              <div className="dpt-editar-btn-wrap">
                <Button
                  variant="outline"
                  size="base"
                  iconLeft={<Icon icon={Storefront} size={16} weight="regular" />}
                  onClick={openLojistasModal}
                >
                  {lojistasCountThis} Lojistas vinculados
                </Button>
                <Button
                  variant="fill"
                  size="base"
                  onClick={() => navigate(`/backoffice/taxas-company/plano/${planoId}/edicao-massa`)}
                >
                  Editar
                </Button>
              </div>
            )}
          </div>

          <div className="dpt-subheader">
            <Pills label="Plano Default" state="success" variant="fill" size="base" icon={Certificate} />
            <div className="dpt-historico-pill-wrap" ref={historicoPillRef}>
              <button
                type="button"
                className={`dpt-historico-pill${historicoDropdownOpen ? ' is-open' : ''}`}
                onClick={() => setHistoricoDropdownOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={historicoDropdownOpen}
              >
                <Icon icon={ClockCounterClockwise} size={14} weight="regular" />
                <span className="dpt-historico-pill-label">
                  {viewingHistoric ? `Histórico — ${viewingHistoric.date}` : 'Versão atual'}
                </span>
                <Icon icon={CaretDown} size={12} weight="bold" />
              </button>
              {historicoDropdownOpen && (
                <div className="dpt-historico-dropdown-card">
                  {(() => {
                    const isCurrentSelected = viewingHistoric === null;
                    return (
                      <button
                        type="button"
                        className={`dpt-historico-item${isCurrentSelected ? ' is-selected' : ''}`}
                        onClick={() => {
                          setHistoricoDropdownOpen(false);
                          setViewingHistoric(null);
                        }}
                      >
                        <Icon icon={ClockCounterClockwise} size={16} weight="regular" />
                        <span className="dpt-historico-item-label">Versão atual</span>
                        {isCurrentSelected && <span className="dpt-historico-item-check">●</span>}
                      </button>
                    );
                  })()}
                  {HISTORICO_ENTRIES.map((h) => {
                    const isSelected = viewingHistoric?.id === h.id;
                    return (
                      <button
                        key={h.id}
                        type="button"
                        className={`dpt-historico-item${isSelected ? ' is-selected' : ''}`}
                        onClick={() => {
                          setHistoricoDropdownOpen(false);
                          setViewingHistoric(h);
                        }}
                      >
                        <Icon icon={ClockCounterClockwise} size={16} weight="regular" />
                        <span className="dpt-historico-item-label">{h.date}</span>
                        {isSelected && <span className="dpt-historico-item-check">●</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {viewingHistoric && (
            <div className="dpt-historic-banner">
              <div className="dpt-historic-banner-icon">
                <Icon icon={ClockCounterClockwise} size={20} weight="regular" />
              </div>
              <div className="dpt-historic-banner-text">
                <Text variant="label-base">
                  Visualizando versão anterior — {viewingHistoric.date}
                </Text>
                <Text variant="caption-md">
                  Alteração feita por {viewingHistoric.author}. As células abaixo mostram o diff em relação à versão atual.
                </Text>
              </div>
              <div className="dpt-historic-banner-actions">
                <Button variant="fill" size="sm" onClick={exitHistoricView}>
                  Restaurar
                </Button>
              </div>
            </div>
          )}

          {/* ── 4 cards de taxas base (PIX, Boleto, Antifraude, Processamento) ── */}
          <div className="dpt-mini-cards-grid">
            {([
              { key: 'pix',         label: 'PIX',                  value: 'R$ 0,70' },
              { key: 'boleto',      label: 'Boleto',               value: 'R$ 1,50' },
              { key: 'antifraude',  label: 'Antifraude',           value: 'R$ 0,29' },
              { key: 'gateway',     label: 'Processamento',        value: 'R$ 0,29' },
            ] as const).map((card) => (
              <div key={card.key} className="dpt-mini-card">
                <Text variant="caption-sm" className="dpt-mini-card-title">{card.label}</Text>
                {editMode && editValues ? (
                  <input
                    className="dpt-edit-input"
                    value={editValues.miniCards[card.key]}
                    onChange={(e) =>
                      setEditValues({
                        ...editValues,
                        miniCards: { ...editValues.miniCards, [card.key]: e.target.value },
                      })
                    }
                  />
                ) : (
                  <Text variant="body-lg" className="dpt-mini-card-value">{card.value}</Text>
                )}
              </div>
            ))}
          </div>


          {/* ── Título "Taxas de Cartões" + Tabs de dia de repasse ──────── */}
          <div className="dpt-section-header">
            <Text variant="label-md" className="dpt-section-title">
              Taxas de Cartões
            </Text>
            <div className="dpt-d-tabs">
              <Tabs>
                {['D+1', 'D+2', 'D+3', 'D+10', 'D+14', 'D+30'].map((d) => (
                  <Tabs.Tab
                    key={d}
                    active={selectedRepasseDay === d}
                    onClick={() => setSelectedRepasseDay(d)}
                  >
                    {d}
                  </Tabs.Tab>
                ))}
              </Tabs>
            </div>
          </div>


          {editMode && (
            <div className="dpt-add-group-row">
              <Button
                variant="outline"
                size="base"
                iconLeft={<Icon icon={Plus} size={16} weight="bold" />}
                onClick={() => {}}
                aria-label="Adicionar grupo de MCC"
              >
                Grupo
              </Button>
            </div>
          )}

          {/* ── Grupos empilhados ────────────────────────────────────────── */}
          {RATES_MOCK.map((g, gIdx) => {
            const isDefault = g.groupId === defaultGroupId;
            const currentMatrix = g.tipo === 'pos' ? g.credito.pos : g.credito.online;
            const historicMatrixData = viewingHistoric ? historicMatrix(viewingHistoric.id, currentMatrix) : null;
            const matrix = currentMatrix;
            return (
              <React.Fragment key={g.groupId}>
                <div className="dpt-group-block">
                  <div className="dpt-group-header-card">
                    <div className="dpt-group-pills-row">
                      <Pills
                        label={`Grupo ${gIdx + 1}`}
                        state="default"
                        variant="fill"
                        size="sm"
                      />
                      {isDefault && (
                        <Pills label="Default" state="success" variant="fill" size="sm" />
                      )}
                      {!isDefault && editMode && (
                        <button
                          type="button"
                          className="dpt-group-make-default"
                          onClick={() => setConfirmDefaultGroupId(g.groupId)}
                        >
                          <Pills label="Tornar Default" state="default" variant="light" size="sm" />
                        </button>
                      )}
                      <Pills
                        label={g.tipo === 'online' ? 'Online' : g.tipo === 'pos' ? 'POS' : 'Tap'}
                        state="warning"
                        variant="light"
                        size="sm"
                      />
                      {g.mccs.map((code) => (
                        <Pills key={code} label={code} state="accent" variant="light" size="sm" />
                      ))}
                      <div className="dpt-group-pill-spacer" />
                      <span className="dpt-antecipacao-pill">
                        Antecipação: <strong>{g.antecipacao}</strong>
                      </span>
                    </div>
                  </div>

                  <div className="dpt-grid-wrapper">
                    <div
                      className="dpt-grid"
                      style={{ gridTemplateColumns: `120px repeat(${BANDEIRAS.length}, 1fr)` }}
                    >
                      <div className="dpt-grid-corner" />
                      {BANDEIRAS.map((b) => {
                        const padrao = gIdx % 2 === 0 ? 'Mastercard' : 'Visa';
                        return (
                          <div key={b} className="dpt-grid-col-header">
                            <Text variant="label-sm">{b}</Text>
                            {b === padrao && (
                              <Pills label="Padrão" state="info" variant="fill" size="sm" />
                            )}
                          </div>
                        );
                      })}

                      {/* Linha Débito (primeira linha) */}
                      <div className="dpt-grid-row-header">
                        <Text variant="label-sm">Débito</Text>
                      </div>
                      {BANDEIRAS.map((b, cIdx) => {
                        const val = g.debitoRow?.[cIdx];
                        return (
                          <div key={`debito-${cIdx}`} className="dpt-grid-cell dpt-grid-cell--readonly">
                            <span className="dpt-mdr-cet">
                              {val != null ? `${val.toFixed(2).replace('.', ',')}%` : '—'}
                            </span>
                          </div>
                        );
                      })}

                      {(() => {
                        // Grupo 3 vem expandido por padrão (mostra 1x..24x individualmente)
                        const isExpanded = gIdx === 2;
                        const parcelasList = isExpanded ? PARCELAS_EXPANDED : PARCELAS;
                        return parcelasList.map((p, rIdx) => {
                          // Quando expandido, o rIdx vai de 0..23; precisamos mapear pro range (0..3)
                          const matrixRowIdx = isExpanded ? parcelaIdxToRangeIdx(rIdx) : rIdx;
                          return (
                            <React.Fragment key={p}>
                              <div className="dpt-grid-row-header">
                                <Text variant="label-sm">{p}</Text>
                              </div>
                              {BANDEIRAS.map((b, cIdx) => {
                                const mdr = matrix[matrixRowIdx]?.[cIdx];
                                const antBase = ANTECIPACAO_BASE_BY_GROUP[g.groupId] ?? 0;
                                const dayMult = DAY_MULTIPLIER[selectedRepasseDay] ?? 0;
                                const cet = mdr != null ? mdr + antBase * dayMult : null;
                                const fmtNum = (n: number) => `${n.toFixed(2).replace('.', ',')}%`;
                                // Modo histórico — mostra diff (versão antiga → versão atual)
                                if (historicMatrixData) {
                                  const oldVal = historicMatrixData[matrixRowIdx]?.[cIdx];
                                  const sameVal = oldVal != null && mdr != null && Math.abs(oldVal - mdr) < 0.001;
                                  return (
                                    <div key={`${rIdx}-${cIdx}`} className="dpt-grid-cell dpt-grid-cell--diff">
                                      {oldVal != null && mdr != null ? (
                                        sameVal ? (
                                          <span className="dpt-diff-same">{fmtNum(mdr)}</span>
                                        ) : (
                                          <span className="dpt-diff-changed">
                                            <span className="dpt-diff-old">{fmtNum(oldVal)}</span>
                                            <Icon icon={ArrowRight} size={11} weight="bold" />
                                            <span className="dpt-diff-new">{fmtNum(mdr)}</span>
                                          </span>
                                        )
                                      ) : (
                                        <span className="dpt-diff-same">—</span>
                                      )}
                                    </div>
                                  );
                                }
                                if (editMode && editValues && g.groupId === grupoId) {
                                  return (
                                    <div key={`${rIdx}-${cIdx}`} className="dpt-grid-cell dpt-grid-cell--edit">
                                      <input
                                        className="dpt-edit-input dpt-edit-input--cell"
                                        value={editValues.matrix[matrixRowIdx]?.[cIdx] ?? ''}
                                        onChange={(e) => {
                                          const next = editValues.matrix.map((row) => row.slice());
                                          if (!next[matrixRowIdx]) next[matrixRowIdx] = ['', '', '', '', ''];
                                          next[matrixRowIdx][cIdx] = e.target.value;
                                          setEditValues({ ...editValues, matrix: next });
                                        }}
                                      />
                                    </div>
                                  );
                                }
                                return (
                                  <button
                                    key={`${rIdx}-${cIdx}`}
                                    type="button"
                                    className="dpt-grid-cell dpt-grid-cell-btn"
                                    onClick={() => {
                                      setGrupoId(g.groupId);
                                      setDrawerParcelaIdx(matrixRowIdx);
                                      setDrawerBandeiraIdx(cIdx);
                                      setCellDrawerOpen(true);
                                    }}
                                    aria-label={`Ver taxas calculadas de ${p} ${b}`}
                                  >
                                    <span className="dpt-mdr-cet">
                                      {mdr != null ? (
                                        <>
                                          {fmtNum(mdr)} <span className="dpt-mdr-cet-total">({cet != null ? fmtNum(cet) : '—'})</span>
                                        </>
                                      ) : '—'}
                                    </span>
                                  </button>
                                );
                              })}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
                {gIdx < RATES_MOCK.length - 1 && <div className="dpt-group-divider" />}
              </React.Fragment>
            );
          })}

        </div>
      </main>
    </BackofficeLayout>

      {/* ── Drawer: taxas calculadas por dia de repasse ─────────────────── */}
      <Drawer
        isOpen={cellDrawerOpen}
        title="Taxas calculadas"
        onClose={() => setCellDrawerOpen(false)}
        footer={{
          primaryButton: {
            children: 'Fechar',
            onClick: () => setCellDrawerOpen(false),
          },
        }}
      >
        <div className="dpt-cell-drawer-body">
          <div className="dpt-cell-drawer-context">
            <Pills label={grupoAtual.groupLabel} state="default" variant="light" size="sm" />
            <Pills label={tipoPagamento === 'credito-online' ? 'Online' : 'POS'} state="accent" variant="light" size="sm" />
          </div>

          <div className="dpt-cell-drawer-filters">
            <Select
              label="Parcela"
              value={String(drawerParcelaIdx)}
              onChange={(v) => setDrawerParcelaIdx(parseInt(v, 10))}
              options={PARCELAS.map((p, idx) => ({ value: String(idx), label: p }))}
            />
            <Select
              label="Bandeira"
              value={String(drawerBandeiraIdx)}
              onChange={(v) => setDrawerBandeiraIdx(parseInt(v, 10))}
              options={BANDEIRAS.map((b, idx) => ({ value: String(idx), label: b }))}
            />
          </div>

          {(() => {
            const baseRate = creditoMatrix[drawerParcelaIdx]?.[drawerBandeiraIdx] ?? 0;
            type Row = { dia: string; antecipacao: number; total: number };
            const rows: Row[] = REPASSE_DAYS.map((d) => {
              const ant = ANTECIPACAO_DELTA[d] ?? 0;
              return { dia: d, antecipacao: ant, total: baseRate + ant };
            });
            const fmt = (n: number) => `${n.toFixed(2).replace('.', ',')}%`;
            const cols: ColumnDefinition<Row>[] = [
              {
                key: 'dia',
                title: 'Dia de repasse',
                dataIndex: 'dia',
                width: 140,
                render: (v) => <Text variant="label-sm">{v as string}</Text>,
              },
              {
                key: 'antecipacao',
                title: 'Taxa de antecipação',
                dataIndex: 'antecipacao',
                render: (v) => <Text variant="body-sm">{fmt(v as number)}</Text>,
              },
              {
                key: 'total',
                title: 'Taxa total',
                dataIndex: 'total',
                render: (v) => <Text variant="label-sm">{fmt(v as number)}</Text>,
              },
            ];
            return (
              <div className="dpt-cell-drawer-table">
                <Table<Row>
                  data={rows}
                  columns={cols}
                  rowKey={(r) => r.dia}
                />
              </div>
            );
          })()}
        </div>
      </Drawer>

      {/* ── Modal: Lojistas vinculados ─────────────────────────────────── */}
      <Modal
        isOpen={sellersModalOpen}
        title="Lojistas vinculados"
        onClose={cancelLojistasModal}
        size="lg"
        footer={{
          primaryButton: {
            children: 'Salvar e fechar',
            onClick: saveAndCloseLojistasModal,
            disabled: !lojistasHasChanges,
          },
          secondaryButton: {
            children: 'Cancelar',
            variant: 'outline',
            onClick: cancelLojistasModal,
          },
        }}
      >
        <div className="dpt-lojistas-modal">
          {/* Tabs + Search + botão de ação alinhados na mesma linha */}
          <div className="dpt-lojistas-tabs-row">
            <div className="dpt-lojistas-tabs">
              <Tabs>
                <Tabs.Tab
                  active={lojistasTab === 'neste'}
                  onClick={() => setLojistasTab('neste')}
                >
                  Neste plano ({lojistasCountThis})
                </Tabs.Tab>
                <Tabs.Tab
                  active={lojistasTab === 'outro'}
                  onClick={() => setLojistasTab('outro')}
                >
                  Em outro plano ({lojistasState.length - lojistasCountThis})
                </Tabs.Tab>
              </Tabs>
            </div>
            <div className="dpt-lojistas-tabs-actions">
              <Search
                expanded={lojistasSearchExpanded}
                value={lojistasQuery}
                onChange={setLojistasQuery}
                onExpand={(expanded) => {
                  setLojistasSearchExpanded(expanded);
                  if (!expanded) setLojistasQuery('');
                }}
                placeholder="Buscar por nome, CNPJ ou plano"
              />
              {lojistaActionMode && (
                <Button variant="fill" size="sm" onClick={applyLojistasAction}>
                  {lojistaActionMode === 'vincular'
                    ? `Vincular ${selectedLojistaIds.size} ${selectedLojistaIds.size === 1 ? 'lojista' : 'lojistas'}`
                    : `Desvincular ${selectedLojistaIds.size} ${selectedLojistaIds.size === 1 ? 'lojista' : 'lojistas'}`}
                </Button>
              )}
            </div>
          </div>

          <div className="dpt-lojistas-table">
            <Table<Lojista>
              data={lojistasFiltered}
              rowKey={(r) => r.id}
              emptyText={
                lojistasTab === 'neste'
                  ? 'Não há lojistas utilizando ainda este plano de taxas.'
                  : 'Nenhum lojista encontrado.'
              }
              columns={[
                {
                  key: 'select',
                  title: (
                    <Checkbox
                      checked={selectAllState === 'all'}
                      indeterminate={selectAllState === 'some'}
                      disabled={selectAllEligibleIds.length === 0}
                      onChange={toggleSelectAllLojistas}
                    />
                  ) as unknown as string,
                  dataIndex: 'id',
                  width: 44,
                  render: (_v, r) => {
                    const disabled =
                      (lojistaActionMode === 'vincular' && r.vinculo !== 'other') ||
                      (lojistaActionMode === 'desvincular' && r.vinculo !== 'this');
                    return (
                      <Checkbox
                        checked={selectedLojistaIds.has(r.id)}
                        disabled={disabled}
                        onChange={() => toggleLojistaSelection(r.id, r.vinculo)}
                      />
                    );
                  },
                },
                {
                  key: 'nome',
                  title: 'Nome',
                  dataIndex: 'nome',
                  render: (v) => <Text variant="body-sm">{v as string}</Text>,
                },
                {
                  key: 'cnpj',
                  title: 'CNPJ',
                  dataIndex: 'cnpj',
                  width: 180,
                  render: (v) => <Text variant="body-sm">{v as string}</Text>,
                },
                {
                  key: 'vinculo',
                  title: 'Vínculo',
                  dataIndex: 'vinculo',
                  width: 220,
                  render: (_v, r) =>
                    r.vinculo === 'this' ? (
                      <Pills label="Neste plano" state="success" variant="light" size="sm" />
                    ) : (
                      <Pills label={`Plano: ${r.planoNome}`} state="default" variant="light" size="sm" />
                    ),
                },
              ]}
            />
          </div>
        </div>
      </Modal>

      {/* ── Modal: confirmar troca do grupo default ──────────────────── */}
      <Modal
        isOpen={confirmDefaultGroupId !== null}
        title="Trocar grupo default"
        onClose={() => setConfirmDefaultGroupId(null)}
        size="sm"
        footer={{
          primaryButton: {
            children: 'Confirmar',
            onClick: () => {
              if (confirmDefaultGroupId) setDefaultGroupId(confirmDefaultGroupId);
              setConfirmDefaultGroupId(null);
            },
          },
          secondaryButton: {
            children: 'Cancelar',
            variant: 'outline',
            onClick: () => setConfirmDefaultGroupId(null),
          },
        }}
      >
        <Text variant="body-sm">
          Confirma mudar o plano default de <strong>{labelGrupo(defaultGroupId)}</strong> para{' '}
          <strong>{confirmDefaultGroupId ? labelGrupo(confirmDefaultGroupId) : ''}</strong>?
        </Text>
      </Modal>

      {/* ── Modal: histórico de alterações (placeholder) ───────────────── */}
      <Modal
        isOpen={historicoModalOpen}
        title="Histórico de alterações"
        onClose={() => setHistoricoModalOpen(false)}
        size="lg"
        footer={{
          primaryButton: {
            children: 'Fechar',
            onClick: () => setHistoricoModalOpen(false),
          },
        }}
      >
        <div className="dpt-historico-placeholder">
          <Icon icon={ClockCounterClockwise} size={32} weight="regular" />
          <Text variant="body-base">
            O histórico de alterações deste plano será especificado em entrega futura.
          </Text>
          <Text variant="caption-md">
            Mostrará data, responsável e o que mudou em cada edição (taxas, MCCs, grupos).
          </Text>
        </div>
      </Modal>
    </>
  );
}
